/**
 * 在VR中使用的软键盘，参考 https://blog.csdn.net/AlbenXie/article/details/103604985
 */
import {
  MeshBasicMaterial,
  Mesh,
  TextureLoader,
  PlaneGeometry,
  CircleGeometry,
  Object3D,
  Vector2,
} from "three";
import { VBase } from "./VBase";
import VUtil from "../VUtils";
import pinyinUtil from "../pinyin/pinyinUtil.js";
import { VTabControl_Text } from "./_VTabControl_Text.js";
import { VScrollViewHonrizontal } from "./_VScrollViewHorizontal.js";
import { HorizontalAlignment, VHorizontalLayout } from "../UILayout/VLayout.js";
import { VContainer } from "./_VContainer.js";

const ContentType = {
  Standard: "Standard", // 没有限制
  IntegerNumber: "IntegerNumber", // 整数，包括负整数
  DecimalNumber: "DecimalNumber", // 实数
};

/**
 * 使用案例
 * 
 *   // 显示键盘，可以输入任意字符
      // this.uiLib.showKeyboard("不满意", "Standard", (str) => {
      //   console.log("输入的内容为："+str);
      // });

      // 显示键盘，只能输入整数
      // this.uiLib.showKeyboard("-123", "IntegerNumber", (str) => {
      //   console.log("输入的内容为：" + str);
      // });

      // 显示键盘，只能输入实数
      this.uiLib.showKeyboard("-123.456", "DecimalNumber", (str) => {
        console.log("输入的内容为：" + str);
      });
 * 
 */

class VSoftKeyboard extends VBase {
  constructor(vuiLib) {
    const tempOptions = {
      renderOrder: 999999,
      width: 2,
      height: 1,
      radius: 0.08,
      color: 0x3e3e3e,
      textBgColor1: 0x4c4c4c,
      textBgColor2: 0x333333,
      textWidth: 0.17,
      textHeight: 0.13,
      textSize: 0.05,
    };

    super(tempOptions, vuiLib);
    this._vType = "VSoftKeyboard";

    this._otherProps = {
      textBgColor1: tempOptions.textBgColor1,
      textBgColor2: tempOptions.textBgColor2,
      textWidth: tempOptions.textWidth,
      textHeight: tempOptions.textHeight,
    };

    this.btnPriority = -2;

    this.currentMode = 1; // 中英文切换，1：中文  2：英文
    this.upperLowerMode = "lower"; // 大小写 "lower" "upper"
    this.currentResult = ""; // 当前输入的内容
    this.currentTextObj = null; // 当前输入的内容对象
    this.currentPinyin = ""; // 当前输入的拼音
    this.currentPinyinObj = ""; // 当前输入的拼音
    this.tempSelectHanzi = ""; // 临时选择的汉字
    this.cursor = null; // 输入的光标
    this.cursorTime = 0;
    this.cursorUpdateTime = 0.5;
    this.pageSize = 5; // 单页显示的文字数量
    this.currentShowHanziArray = []; // 当前正在显示的备选汉字列表
    this.hanziPageMap = new Map(); // 按页存储当前的备选汉字列表，只有查看了的页会缓存，切换拼音后这个map会清空

    this.contentType = ContentType.Standard; // 输入的内容类型
    if (
      this.contentType === ContentType.IntegerNumber ||
      this.contentType === ContentType.DecimalNumber
    ) {
      this.currentMode = 2;
    }

    this.position.z = 0.05; // 让软键盘靠在最前面，避免被其他UI遮挡

    this.name = "VSoftKeyboard";

    this.btns1 = []; // 存储字母区按钮
    this.btns2 = []; // 存储符号区按钮
    this.btns3 = []; // 存储常用字符串区按钮
    this.latterBtns = [];

    this.latterArea = new Object3D(); // 字母数字区
    this.add(this.latterArea);
    this.symbolArea = new Object3D(); // 符号区
    this.symbolArea.visible = false;
    this.add(this.symbolArea);
    this.commonArea = new Object3D(); // 常用字符串区
    this.commonArea.visible = false;
    this.add(this.commonArea);

    // 拼音候选区
    {
      const geo = VUtil.createRectangleGeometry(
        this._baseProps.width / 2,
        this._otherProps.textHeight,
        0.07
      );

      const mat = new MeshBasicMaterial({
        color: this._baseProps.color,
        transparent: true,
        depthTest: this.depthTest,
      });
      VUtil.convertSRGBToLinear(mat);
      const obj = new Mesh(geo, mat);
      this.add(obj);
      obj.position.y =
        this._baseProps.height / 2 + this._otherProps.textHeight / 2 + 0.02;
      this.pinyinShower = obj;
      obj.visible = false;
      if (this.vuilib) this.vuilib.addComponents(obj, this.btnPriority - 1);
      // 注册光标进入事件
      obj.onEcsyHoverEnter = () => {
        this.onHoverEnter();
      };
      // 注册光标退出事件
      obj.onEcsyHoverExit = () => {
        this.onHoverExit();
      };
    }

    // 汉字候选区
    {
      const geo = VUtil.createRectangleGeometry(
        this._baseProps.width / 2,
        this._otherProps.textHeight,
        0.07
      );

      const mat = new MeshBasicMaterial({
        color: this._baseProps.color,
        transparent: true,
        depthTest: this.depthTest,
      });
      VUtil.convertSRGBToLinear(mat);
      const obj = new Mesh(geo, mat);
      this.add(obj);
      obj.position.y =
        this._baseProps.height / 2 + this._otherProps.textHeight * 2 - 0.025;
      this.hanziShower = obj;
      obj.visible = false;
      if (this.vuilib) this.vuilib.addComponents(obj, this.btnPriority - 1);
      // 注册光标进入事件
      obj.onEcsyHoverEnter = () => {
        this.onHoverEnter();
      };
      // 注册光标退出事件
      obj.onEcsyHoverExit = () => {
        this.onHoverExit();
      };

      this.hanziBgGeo = new CircleGeometry(0.05, 32);
      this.hanziMat = new MeshBasicMaterial({
        color: this._otherProps.textBgColor1,
        transparent: true,
        depthTest: this.depthTest,
      });
      VUtil.convertSRGBToLinear(this.hanziMat);

      // 下一页按钮
      {
        const pageBtn = new Mesh(this.hanziBgGeo, this.hanziMat);
        pageBtn.renderOrder = this._baseProps.renderOrder;
        pageBtn.position.z = 0.001;
        pageBtn.position.x = this._baseProps.height / 2 + 0.07;
        this.hanziShower.add(pageBtn);

        pageBtn.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        pageBtn.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        pageBtn.onVRClick = () => {
          if (this.pageCurrent < this.pageCount) {
            this.pageCurrent++;
            if (this.pageCurrent === 2) this.prePageBtn.visible = true;
            if (this.pageCurrent === this.pageCount)
              this.nextPageBtn.visible = false;
            this._refreshPage();
          }
        };
        if (this.vuilib) this.vuilib.addComponents(pageBtn, this.btnPriority);

        // text
        const textObj = VUtil.createText(
          ">",
          this._textProps.textSize,
          0xffffff,
          "normal"
        );
        textObj.renderOrder = this._baseProps.renderOrder;
        pageBtn.add(textObj);
        this.nextPageBtn = pageBtn;
        this.nextPageBtn.visible = false;
      }
      // 上一页按钮
      {
        const pageBtn = new Mesh(this.hanziBgGeo, this.hanziMat);
        pageBtn.renderOrder = this._baseProps.renderOrder;
        pageBtn.position.z = 0.001;
        pageBtn.position.x = -this._baseProps.height / 2 - 0.07;
        this.hanziShower.add(pageBtn);

        pageBtn.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        pageBtn.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        pageBtn.onVRClick = () => {
          if (this.pageCurrent > 1) {
            this.pageCurrent--;

            if (this.pageCurrent === this.pageCount - 1)
              this.nextPageBtn.visible = true;
            if (this.pageCurrent === 1) this.prePageBtn.visible = false;

            this._refreshPage();
          }
        };
        if (this.vuilib) this.vuilib.addComponents(pageBtn, this.btnPriority);

        // text
        const textObj = VUtil.createText(
          "<",
          this._textProps.textSize,
          0xffffff,
          "normal"
        );
        textObj.renderOrder = this._baseProps.renderOrder;
        pageBtn.add(textObj);
        this.prePageBtn = pageBtn;
        this.prePageBtn.visible = false;
      }
    }

    // 输入结果区
    {
      this.resultShowerWidth = (this._baseProps.width / 10) * 9;
      const layout = new VHorizontalLayout();
      layout.setAliment(HorizontalAlignment.Center);
      layout.setBorder(new Vector2(0.05, 0));
      const scrollview = new VScrollViewHonrizontal(
        {
          color: this._otherProps.textBgColor1,
          opacity: 1,
          showSlider: false,
          horizontalLayout: layout,
          width: this.resultShowerWidth,
          height: this._otherProps.textHeight,
          radius: 0.05,
        },
        this.vuilib
      );
      scrollview.addOnSliderValueChangeEvent((v) => {
        if (this.currentTextObj.width > this.resultShowerWidth) {
          if (v > 0.99) this.cursor.material.visible = true;
          else this.cursor.material.visible = false;
        }
      });
      this.add(scrollview);
      scrollview.renderOrder = this._baseProps.renderOrder;
      scrollview.position.y = 0.4;
      scrollview.position.z = 0.001;
      //scrollview.position.x = 0.05;
      this.resultShower = scrollview;
    }

    // 光标
    {
      this.cursor = VUtil.createText(
        "|",
        this._textProps.textSize,
        0xffffff,
        "normal",
        (tex) => {
          this.cursor.position.x += -this.resultShowerWidth / 2 + 0.05;
          this.cursor._defaultPos = this.cursor.position.x;
        }
      );
      this.cursor.renderOrder = this._baseProps.renderOrder;

      this.resultShower.add(this.cursor);
    }

    this._createKey();

    this._createSymbolKey();

    this._createStrKey();

    this.width = this._baseProps.width;
    this.height = this._baseProps.height;
  }

  clear() {
    console.error("clear");
    this._resetPinyin();
    this.chSymbolArray = []; // 缓存中文符号
    this.enSymbolArray = []; // 缓存英文符号
    this.btns1 = []; // 存储字母区按钮
    this.btns2 = []; // 存储符号区按钮
    this.btns3 = []; // 存储常用字符串区按钮
    this.latterBtns = [];

    this.currentMode = 1; // 中英文切换，1：中文  2：英文
    this.upperLowerMode = "lower"; // 大小写 "lower" "upper"
    this.currentResult = ""; // 当前输入的内容
    this.currentTextObj = null; // 当前输入的内容对象
    this.currentPinyin = ""; // 当前输入的拼音
    this.currentPinyinObj = ""; // 当前输入的拼音
    this.tempSelectHanzi = ""; // 临时选择的汉字
    this.cursor = null; // 输入的光标
    this.cursorTime = 0;
    this.cursorUpdateTime = 0.5;
    this.pageSize = 5; // 单页显示的文字数量
    this.currentShowHanziArray = []; // 当前正在显示的备选汉字列表
    this.hanziPageMap.clear();

    super.clear();
  }

  /**
   * 初始化软键盘
   */
  _reset() {
    this._resetPinyin();

    this.currentPinyin = "";

    if (!this.latterArea.visible) {
      this.latterArea.visible = true;
      this.symbolArea.visible = false;
      this.commonArea.visible = false;
    }

    if (this.currentMode !== 1) {
      this._changeChEn();
    }
    if (this.upperLowerMode === "upper") {
      this._changeLowerUpper();
      this.symbolMode = 0;
    }
  }

  _hide() {
    this._reset();

    super.hide();
  }

  /**
   * 显示软键盘
   * @param {String} defaultStr 原始存在的字符串内容
   * @param {ContentType} contentType 输入的内容类型，目前支持 ContentType.Standard 标准输入，ContentType.IntegerNumber 整数，ContentType.DecimalNumber 实数
   * @param {Function} callback 回调函数，软键盘输入完成点击回车键后调用的，带一个参数，最终的字符串内容
   */
  show(defaultStr, contentType = ContentType.Standard, callback = null) {
    super.show();

    this.contentType = contentType;

    this.currentResult = defaultStr ? defaultStr : "";

    if (
      this.contentType !== ContentType.Standard &&
      this.contentType !== ContentType.IntegerNumber &&
      this.contentType !== ContentType.DecimalNumber
    ) {
      this.contentType = ContentType.Standard;
    }

    if (
      this.contentType === ContentType.DecimalNumber ||
      this.contentType === ContentType.IntegerNumber
    ) {
      this.switchBtn.visible = false;
      this.languageBtn.visible = false;
      this.ofenUseBtn.visible = false;
      // 切换到小写
      if (this.upperLowerMode === "upper") {
        this._changeLowerUpper();
      }
      // 切换到英文
      if (this.currentMode === 1) {
        this._changeChEn();
      }

      this.currentResult = this._extractAndConvertToNumber(this.currentResult);

      if (
        this.contentType === ContentType.IntegerNumber &&
        this.currentResult.indexOf(".") > -1
      ) {
        this.currentResult = this.currentResult.substring(
          0,
          this.currentResult.indexOf(".")
        );
      }
    } else {
      this.switchBtn.visible = true;
      this.languageBtn.visible = true;
      this.ofenUseBtn.visible = true;
      this.signBtn.visible = true;
    }

    this._updateResultShower();
    this.callback = callback;
  }

  _createKey() {
    // 第一排  数字键
    {
      const texts = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
      for (var i = 0; i < texts.length; i++) {
        const item = this._createNumberItem(
          texts[i],
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor1
        );
        item.position.x =
          -this._baseProps.width / 2 +
          0.06 +
          i * this._otherProps.textWidth +
          i * 0.02 +
          this._otherProps.textWidth / 2;
        item.position.y =
          this._otherProps.textHeight * 2 +
          0.045 -
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = (target) => {
          this._onClickNumberKey(target.name);
        };
      }
    }
    // 第二排
    {
      const texts = ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"];
      for (var i = 0; i < texts.length; i++) {
        const item = this._createLatterItem(
          texts[i],
          texts[i].toUpperCase(),
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor1
        );
        item.position.x =
          -this._baseProps.width / 2 +
          0.06 +
          i * this._otherProps.textWidth +
          i * 0.02 +
          this._otherProps.textWidth / 2;
        item.position.y =
          this._otherProps.textHeight + 0.015 - this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);
        this.latterBtns.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = (target) => {
          this._onClickLatterKey(target.name);
        };
      }
    }
    // 第三排
    {
      const texts = ["a", "s", "d", "f", "g", "h", "j", "k", "l"];
      for (var i = 0; i < texts.length; i++) {
        const item = this._createLatterItem(
          texts[i],
          texts[i].toUpperCase(),
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor1
        );
        item.position.x =
          -this._baseProps.width / 2 +
          0.07 +
          i * this._otherProps.textWidth +
          i * 0.02 +
          this._otherProps.textWidth;
        item.position.y =
          -this._otherProps.textHeight -
          0.015 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);
        this.latterBtns.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = (target) => {
          this._onClickLatterKey(target.name);
        };
      }
    }
    // 第四排
    {
      const texts = ["z", "x", "c", "v", "b", "n", "m"];
      for (var i = 0; i < texts.length; i++) {
        const item = this._createLatterItem(
          texts[i],
          texts[i].toUpperCase(),
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor1
        );
        item.position.x =
          -this._baseProps.width / 2 +
          0.09 +
          i * this._otherProps.textWidth +
          i * 0.02 +
          this._otherProps.textWidth * 2;
        item.position.y =
          -this._otherProps.textHeight * 2 -
          0.045 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);
        this.latterBtns.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = (target) => {
          this._onClickLatterKey(target.name);
        };
      }
      // 大小写切换
      {
        const item = this._createImageItem(
          "/assets/vrUILib/softKeyboardImages/daxiaoxie.png",
          0.22,
          this._otherProps.textHeight,
          0.06,
          this._otherProps.textBgColor2
        );
        item.position.x = -0.825;
        item.position.y =
          -this._otherProps.textHeight * 2 -
          0.045 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);
        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);
        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = () => {
          this._changeLowerUpper();
        };

        this.switchBtn = item;
      }
      // 退格键
      {
        const item = this._createImageItem(
          "/assets/vrUILib/softKeyboardImages/delete.png",
          0.22,
          this._otherProps.textHeight,
          0.07,
          this._otherProps.textBgColor2
        );
        item.position.x = 0.825;
        item.position.y =
          -this._otherProps.textHeight * 2 -
          0.045 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);
        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);
        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = () => {
          this._onClickBackKey();
        };
      }
    }
    // 第五排
    {
      // 符号键
      {
        const item = this._createImageItem(
          "/assets/vrUILib/softKeyboardImages/simble.png",
          0.26,
          this._otherProps.textHeight,
          0.07,
          this._otherProps.textBgColor2
        );
        item.position.x = -0.81;
        item.position.y =
          -this._otherProps.textHeight * 3 -
          0.075 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);
        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);
        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = () => {
          this.latterArea.visible = false;
          this.symbolArea.visible = true;

          if (
            this.currentPinyin.length > 0 ||
            this.tempSelectHanzi.length > 0
          ) {
            this.currentResult += this.tempSelectHanzi + this.currentPinyin;
            this._updateResultShower();
            this._resetPinyin();
          }
        };

        this.signBtn = item;
      }
      {
        const texts = ["/", "."];
        for (var i = 0; i < texts.length; i++) {
          const item = this._createNumberItem(
            texts[i],
            this._otherProps.textWidth,
            this._otherProps.textHeight,
            this._otherProps.textBgColor2
          );
          item.position.x =
            -this._baseProps.width / 2 +
            0.09 +
            i * this._otherProps.textWidth +
            i * 0.02 +
            this._otherProps.textWidth * 2;
          item.position.y =
            -this._otherProps.textHeight * 3 -
            0.075 +
            this._otherProps.textHeight / 2;
          item.position.z = 0.001;
          item.renderOrder = this._baseProps.renderOrder;
          this.latterArea.add(item);

          this.vuilib.addComponents(item, this.btnPriority);
          this.btns1.push(item);

          item.onEcsyHoverEnter = (target) => {
            this._onElementHoverEnter(target);
          };
          item.onEcsyHoverExit = (target) => {
            this._onElementHoverExit(target);
          };
          item.onVRClick = (target) => {
            this._onClickSignKey(target.name);
          };
        }
      }
      // 空格
      {
        const item = this._createNumberItem(
          "Space",
          this._otherProps.textWidth * 3 + 0.04,
          this._otherProps.textHeight,
          this._otherProps.textBgColor1
        );
        item.position.x = 0;
        item.position.y =
          -this._otherProps.textHeight * 3 -
          0.075 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = () => {
          this._onClickSpaceKey();
        };
      }
      // 中英切换
      {
        const item = this._createLatterItem(
          "中",
          "En",
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor2
        );
        item.position.x =
          (this._otherProps.textWidth * 3 + 0.04) / 2 +
          this._otherProps.textWidth / 2 +
          0.02;
        item.position.y =
          -this._otherProps.textHeight * 3 -
          0.075 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);

        if (this.currentMode == 2) {
          item._text1.visible = false;
          item._text2.visible = true;
        }

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = () => {
          this._changeChEn();
        };

        this.languageBtn = item;
      }
      // 常用字符串
      {
        const item = this._createNumberItem(
          "Str",
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor2
        );
        item.position.x =
          (this._otherProps.textWidth * 3 + 0.04) / 2 +
          this._otherProps.textWidth * 1.5 +
          0.02 * 2;
        item.position.y =
          -this._otherProps.textHeight * 3 -
          0.075 +
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.latterArea.add(item);

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns1.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = () => {
          this.latterArea.visible = false;
          this.commonArea.visible = true;
        };

        this.ofenUseBtn = item;
      }
      // 回车
      {
        {
          const item = this._createImageItem(
            "/assets/vrUILib/softKeyboardImages/enter.png",
            0.26,
            this._otherProps.textHeight,
            0.1,
            this._otherProps.textBgColor2
          );
          item.position.x = 0.81;
          item.position.y =
            -this._otherProps.textHeight * 3 -
            0.075 +
            this._otherProps.textHeight / 2;
          item.position.z = 0.001;
          item.renderOrder = this._baseProps.renderOrder;
          this.latterArea.add(item);
          this.vuilib.addComponents(item, this.btnPriority);
          this.btns1.push(item);
          item.onEcsyHoverEnter = (target) => {
            this._onElementHoverEnter(target);
          };
          item.onEcsyHoverExit = (target) => {
            this._onElementHoverExit(target);
          };
          item.onVRClick = () => {
            if (this.currentPinyin.length > 0) {
              this.currentResult += this.tempSelectHanzi + this.currentPinyin;
              this._updateResultShower();
              this._resetPinyin();
            } else {
              if (
                this.contentType === ContentType.IntegerNumber ||
                this.contentType === ContentType.DecimalNumber
              ) {
                if (this.currentResult.length === 0) {
                  this.currentResult = "0";
                } else if (this.currentResult === "-") {
                  this.currentResult = "0";
                } else if (
                  this.currentResult[this.currentResult.length - 1] === "."
                ) {
                  this.currentResult = this.currentResult.substring(
                    0,
                    this.currentResult.length - 1
                  );
                } else {
                  this.currentResult = this._extractAndConvertToNumber(
                    this.currentResult
                  );
                }
              }

              // 将当前输入的字符串发送出去
              if (this.callback) {
                this.callback(this.currentResult);
              }
              this.currentResult = "";
              this.callback = null;

              this._hide();
            }
          };
        }
      }
    }
  }

  /**
   * 创建符号按键
   */
  _createSymbolKey() {
    this.symbolMode = 0; // 当前显示的符号类型  0 中文  1 英文
    // 中文符号
    {
      const chSymbol = [
        "，",
        "。",
        "？",
        "！",
        "、",
        "：",
        "；",
        "……",
        "——",
        "·",
        "￥",
        "《",
        "》",
        "【",
        "】",
        "{",
        "}",
        "“",
        "”",
        "‘",
        "’",
        "（",
        "）",
      ];
      this.chSymbolArray = []; // 缓存中文符号
      for (var i = 0; i < chSymbol.length; i++) {
        const item = this._createNumberItem(
          chSymbol[i],
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor1
        );

        let x = i % 10;
        let y = Math.floor(i / 10);

        item.position.x =
          -this._baseProps.width / 2 +
          0.06 +
          x * this._otherProps.textWidth +
          x * 0.02 +
          this._otherProps.textWidth / 2;
        item.position.y =
          this._otherProps.textHeight * (2 - y) +
          0.045 -
          y * 0.03 -
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.symbolArea.add(item);

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns2.push(item);
        this.chSymbolArray.push(item);

        item.visible = this.symbolMode === 0 ? true : false;

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = (target) => {
          this._onClickSignKey(target.name);
        };
      }
    }

    // 英文符号
    {
      this.enSymbolArray = []; // 缓存英文符号
      const enSymbol = [
        ",",
        ".",
        "?",
        "/",
        "!",
        ";",
        ":",
        "'",
        '"',
        "|",
        "\\",
        "~",
        "`",
        "@",
        "$",
        "#",
        "%",
        "^",
        "&",
        "*",
        "-",
        "--",
        "+",
        "_",
        "(",
        ")",
        "[",
        "]",
        "{",
        "}",
      ];
      for (var i = 0; i < enSymbol.length; i++) {
        const item = this._createNumberItem(
          enSymbol[i],
          this._otherProps.textWidth,
          this._otherProps.textHeight,
          this._otherProps.textBgColor1
        );

        let x = i % 10;
        let y = Math.floor(i / 10);

        item.position.x =
          -this._baseProps.width / 2 +
          0.06 +
          x * this._otherProps.textWidth +
          x * 0.02 +
          this._otherProps.textWidth / 2;
        item.position.y =
          this._otherProps.textHeight * (2 - y) +
          0.045 -
          y * 0.03 -
          this._otherProps.textHeight / 2;
        item.position.z = 0.001;
        item.renderOrder = this._baseProps.renderOrder;
        this.symbolArea.add(item);

        item.visible = this.symbolMode === 1 ? true : false;

        this.vuilib.addComponents(item, this.btnPriority);
        this.btns2.push(item);
        this.enSymbolArray.push(item);

        item.onEcsyHoverEnter = (target) => {
          this._onElementHoverEnter(target);
        };
        item.onEcsyHoverExit = (target) => {
          this._onElementHoverExit(target);
        };
        item.onVRClick = (target) => {
          this._onClickSignKey(target.name);
        };
      }
    }

    // 切换回字母
    {
      const item = this._createNumberItem(
        "Abc",
        0.26,
        this._otherProps.textHeight,
        this._otherProps.textBgColor2
      );
      item.position.x = -0.81;
      item.position.y =
        -this._otherProps.textHeight * 3 -
        0.075 +
        this._otherProps.textHeight / 2;
      item.position.z = 0.001;
      item.renderOrder = this._baseProps.renderOrder;
      this.symbolArea.add(item);

      this.vuilib.addComponents(item, this.btnPriority);
      this.btns2.push(item);

      item.onEcsyHoverEnter = (target) => {
        this._onElementHoverEnter(target);
      };
      item.onEcsyHoverExit = (target) => {
        this._onElementHoverExit(target);
      };
      item.onVRClick = () => {
        this.latterArea.visible = true;
        this.symbolArea.visible = false;
      };
    }

    // 符号切换
    {
      const str = ["中文", "英文"];
      const width = 0.6;
      const tabControl = new VTabControl_Text(
        {
          renderOrder: this._baseProps.renderOrder,
          color: this._otherProps.textBgColor2,
          opacity: 0,
          texts: str,
          btnColor: this._otherProps.textBgColor2,
          textSize: this._textProps.textSize,
          selectColor: this._otherProps.textBgColor1,
          textColor: 0xffffff,
          radius: 0.04,
          width: width,
          gap: 0.03,
          spacing: 0,
          height: this._otherProps.textHeight,
          defaultIndex: this.symbolMode,
        },
        this.vuilib
      );
      tabControl.addOnElementHoverEnterEvent((obj) => {
        this._onElementHoverEnter(obj);
      });
      tabControl.addOnElementHoverExitEvent((obj) => {
        this._onElementHoverExit(obj);
      });
      tabControl.addOnElementClickEvent((obj, index) => {
        if (index === 0) {
          this.symbolMode = 1;
          for (var i = 0; i < this.chSymbolArray.length; i++) {
            this.chSymbolArray[i].visible = true;
          }
          for (var i = 0; i < this.enSymbolArray.length; i++) {
            this.enSymbolArray[i].visible = false;
          }
        } else if (index === 1) {
          this.symbolMode = 2;
          for (var i = 0; i < this.chSymbolArray.length; i++) {
            this.chSymbolArray[i].visible = false;
          }
          for (var i = 0; i < this.enSymbolArray.length; i++) {
            this.enSymbolArray[i].visible = true;
          }
        }
      });
      this.symbolArea.add(tabControl);
      tabControl.position.x = -this._baseProps.width / 2 + width / 2 + 0.35;
      tabControl.position.y =
        -this._otherProps.textHeight * 3 -
        0.075 +
        this._otherProps.textHeight / 2;
      tabControl.position.z = 0.001;
      this.btns2 = this.btns2.concat(tabControl.btns);
    }
  }

  /**
   * 创建常用字符串按键
   */
  _createStrKey() {
    const strs = ["http://", "https://", "192.168.0.0", ".com"];

    for (var i = 0; i < strs.length; i++) {
      const item = this._createNumberItem(
        strs[i],
        this._otherProps.textWidth * 4,
        this._otherProps.textHeight,
        this._otherProps.textBgColor1
      );

      let x = i % 2;
      let y = Math.floor(i / 2);

      if (x === 0) {
        item.position.x = (-this._otherProps.textWidth * 4) / 2 - 0.1;
      } else {
        item.position.x = (this._otherProps.textWidth * 4) / 2 + 0.1;
      }

      item.position.y =
        this._otherProps.textHeight * (2 - y) +
        0.045 -
        y * 0.03 -
        this._otherProps.textHeight / 2;
      item.position.z = 0.001;
      item.renderOrder = this._baseProps.renderOrder;
      this.commonArea.add(item);

      this.vuilib.addComponents(item, this.btnPriority);
      this.btns3.push(item);

      item.visible = this.symbolMode === 0 ? true : false;

      item.onEcsyHoverEnter = (target) => {
        this._onElementHoverEnter(target);
      };
      item.onEcsyHoverExit = (target) => {
        this._onElementHoverExit(target);
      };
      item.onVRClick = (target) => {
        this._onClickNumberKey(target.name);
      };
    }

    // 切换回字母
    {
      const item = this._createNumberItem(
        "Abc",
        0.26,
        this._otherProps.textHeight,
        this._otherProps.textBgColor2
      );
      item.position.x = -0.81;
      item.position.y =
        -this._otherProps.textHeight * 3 -
        0.075 +
        this._otherProps.textHeight / 2;
      item.position.z = 0.001;
      item.renderOrder = this._baseProps.renderOrder;
      this.commonArea.add(item);

      this.vuilib.addComponents(item, this.btnPriority);
      this.btns3.push(item);

      item.onEcsyHoverEnter = (target) => {
        this._onElementHoverEnter(target);
      };
      item.onEcsyHoverExit = (target) => {
        this._onElementHoverExit(target);
      };
      item.onVRClick = () => {
        this.latterArea.visible = true;
        this.commonArea.visible = false;
      };
    }
  }

  /**
   * 切换大小写
   */
  _changeLowerUpper() {
    if (this.upperLowerMode === "lower") {
      this.upperLowerMode = "upper";
      for (var i = 0; i < this.latterBtns.length; i++) {
        this.latterBtns[i]._text1.visible = false;
        this.latterBtns[i]._text2.visible = true;
      }

      // 切换到英文
      if (this.currentMode === 1) {
        this._changeChEn();
      }
    } else {
      this.upperLowerMode = "lower";
      for (var i = 0; i < this.latterBtns.length; i++) {
        this.latterBtns[i]._text1.visible = true;
        this.latterBtns[i]._text2.visible = false;
      }
    }
  }

  /**
   * 切换中英文
   */
  _changeChEn() {
    // 切换到中文
    if (this.currentMode === 2) {
      this.currentMode = 1;
      this.languageBtn._text1.visible = true;
      this.languageBtn._text2.visible = false;

      // 切换到小写
      if (this.upperLowerMode === "upper") {
        this._changeLowerUpper();
      }
    }
    // 切换到英文
    else {
      this.currentMode = 2;
      this.languageBtn._text1.visible = false;
      this.languageBtn._text2.visible = true;

      if (this.currentPinyin.length > 0) {
        this.currentResult += this.tempSelectHanzi + this.currentPinyin;
        this._updateResultShower();
        this._resetPinyin();
      }
    }
  }

  _createNumberItem(text, width, height, color) {
    const geo = VUtil.createRectangleGeometry(width, height, 0.03);
    const mat = new MeshBasicMaterial({
      color: color,
      transparent: true,
      depthTest: this.depthTest,
    });
    VUtil.convertSRGBToLinear(mat);
    let item = new Mesh(geo, mat);
    item.renderOrder = this._baseProps.renderOrder;
    item.name = text;

    const textObj = VUtil.createText(
      text,
      this._textProps.textSize,
      0xffffff,
      "normal"
    );
    textObj.renderOrder = this._baseProps.renderOrder;
    item.add(textObj);
    return item;
  }

  _createLatterItem(text1, text2, width, height, color) {
    const geo = VUtil.createRectangleGeometry(width, height, 0.03);
    const mat = new MeshBasicMaterial({
      color: color,
      transparent: true,
      depthTest: this.depthTest,
    });
    VUtil.convertSRGBToLinear(mat);
    let item = new Mesh(geo, mat);
    item.renderOrder = this._baseProps.renderOrder;
    item.name = text1;

    const textObj = VUtil.createText(
      text1,
      this._textProps.textSize,
      0xffffff,
      "normal"
    );
    textObj.renderOrder = this._baseProps.renderOrder;
    item.add(textObj);

    const textObj2 = VUtil.createText(
      text2,
      this._textProps.textSize,
      0xffffff,
      "normal"
    );
    textObj2.renderOrder = this._baseProps.renderOrder;
    item.add(textObj2);
    textObj2.visible = false;
    item._text1 = textObj;
    item._text2 = textObj2;
    return item;
  }

  _createImageItem(path, width, height, textWidth, color) {
    const geo = VUtil.createRectangleGeometry(width, height, 0.03);
    const mat = new MeshBasicMaterial({
      color: color,
      transparent: true,
      depthTest: this.depthTest,
    });
    VUtil.convertSRGBToLinear(mat);
    const item = new Mesh(geo, mat);
    item.renderOrder = this._baseProps.renderOrder;

    new TextureLoader().load(
      path,
      (tex) => {
        const geo1 = new PlaneGeometry(textWidth, textWidth);
        const mat1 = new MeshBasicMaterial({
          map: tex,
          transparent: true,
          depthTest: this.depthTest,
        });
        const image = new Mesh(geo1, mat1);
        image.renderOrder = this._baseProps.renderOrder;
        item.add(image);
        image.position.z = 0.001;
      },
      null,
      (err) => {
        console.error(err);
      }
    );
    return item;
  }

  _onElementHoverEnter(obj) {
    obj._vrHoverSrcScale = obj.scale.clone();
    obj.scale.multiplyScalar(1.1);
    obj.position.z += 0.02;
  }

  _onElementHoverExit(obj) {
    if (obj._vrHoverSrcScale) {
      obj.scale.copy(obj._vrHoverSrcScale);
      delete obj._vrHoverSrcScale;
      obj.position.z -= 0.02;
    }
  }

  /**
   * 更新输入结果显示
   */
  _updateResultShower() {
    if (this.currentTextObj) {
      if (this.cursor) {
        this.resultShower.add(this.cursor);
        this.cursor.position.x = this.cursor._defaultPos;
      }

      if (
        this.currentTextObj._vType &&
        this.currentTextObj._vType === "VContainer"
      ) {
        this.resultShower.removeContent(this.currentTextObj);
        this.currentTextObj.clear();
      } else {
        this._traverseDispose(this.currentTextObj);
        this.currentTextObj.removeFromParent();
      }

      this.currentTextObj = null;
    }

    if (this.currentResult.length > 0) {
      VUtil.createText(
        this.currentResult, //[...this.currentResult].join(" "), // 用空格分割字符
        this._textProps.textSize,
        0xffffff,
        "normal",
        (tex) => {
          tex.renderOrder = this._baseProps.renderOrder;
          const width = tex._width;
          // tex._width > this.resultShowerWidth
          //   ? tex._width
          //   : this.resultShowerWidth;
          let container = new VContainer({
            width: width,
            height: this._otherProps.textHeight,
            opacity: 0,
          });
          container.add(tex);
          this.resultShower.addContent(container);
          this.currentTextObj = container;
          if (this.currentTextObj.width > this.resultShowerWidth)
            this.resultShower.setSliderValue(1);

          if (this.cursor) {
            container.add(this.cursor);
            this.cursor.position.x = tex._width / 2 + 0.001;
          }
        }
      );
    }
  }

  /**
   * 点击数字键
   * @param {*} text
   */
  _onClickNumberKey(text) {
    this.currentResult += text;
    // 更新结果区
    this._updateResultShower();
  }

  /**
   * 点击符号键
   * @param {*} text
   * @returns
   */
  _onClickSignKey(text) {
    // 限制输入整数时，只能在第一位输入负号
    if (this.contentType === ContentType.IntegerNumber) {
      if (text === "-") {
        if (this.currentResult.length === 0) {
          this.currentResult += text;
          //  更新结果区
          this._updateResultShower();
        }
      }
      return;
    }
    // 限制输入实数时，只能在第一位输入负号，且只能输入一个小数点
    else if (this.contentType === ContentType.DecimalNumber) {
      if (text === "-") {
        if (this.currentResult.length === 0) {
          this.currentResult += text;
          //  更新结果区
          this._updateResultShower();
        }
        return;
      } else if (text === ".") {
        if (
          this.currentResult.indexOf(".") === -1 &&
          this.currentResult.length > 0 &&
          !(this.currentResult.length === 1 && this.currentResult[0] === "-")
        ) {
          this.currentResult += text;
          //  更新结果区
          this._updateResultShower();
        }
        return;
      } else return;
    }

    this.currentResult += text;
    // 更新结果区
    this._updateResultShower();
  }

  /**
   * 如果字符串中包含多个数字部分，可以提取第一个有效的数字部分。
   * @param {*} str
   * @returns
   */
  _extractAndConvertToNumber(str) {
    // 使用正则表达式提取第一个有效的数字部分
    const match = str.match(/-?\d+(\.\d+)?/);

    // 如果找到匹配的部分，转换为数字；否则返回默认值（如 0）
    return match ? parseFloat(match[0]) + "" : ""; // 转成字符串
  }

  /**
   * 点击字母键
   * @param {*} text
   */
  _onClickLatterKey(text) {
    // 输入英文
    if (this.currentMode === 2) {
      // 限制输入整数时，只能在第一位输入负号
      if (this.contentType === ContentType.IntegerNumber) {
        if (text === "-") {
          if (this.currentResult.length === 0) {
            this.currentResult += text;
            //  更新结果区
            this._updateResultShower();
          }
        }
        return;
      }
      // 限制输入实数时，只能在第一位输入负号，且只能输入一个小数点
      else if (this.contentType === ContentType.DecimalNumber) {
        if (text === "-") {
          if (this.currentResult.length === 0) {
            this.currentResult += text;
            //  更新结果区
            this._updateResultShower();
          }
          return;
        } else if (text === ".") {
          if (
            this.currentResult.indexOf(".") === -1 &&
            this.currentResult.length > 0 &&
            !(this.currentResult.length === 1 && this.currentResult[0] === "-")
          ) {
            this.currentResult += text;
            //  更新结果区
            this._updateResultShower();
          }
          return;
        } else return;
      }

      this.currentResult +=
        this.upperLowerMode === "lower" ? text : text.toUpperCase();
      //  更新结果区
      this._updateResultShower();
    }
    // 输入中文
    else {
      // 更新拼音输入
      this._addChar(text);
    }
  }

  /**
   * 点击退格键
   */
  _onClickBackKey() {
    // 输入英文
    if (this.currentMode === 2) {
      this.currentResult = this.currentResult.slice(0, -1);
      this._updateResultShower();
    }
    // 输入中文
    else {
      // 拼音输入区有拼音
      if (this.currentPinyin.length > 0) {
        this._deleteChar();
      } else {
        this.currentResult = this.currentResult.slice(0, -1);
        this._updateResultShower();
      }
    }
  }

  /**
   * 点击空格键
   */
  _onClickSpaceKey() {
    if (
      this.contentType === ContentType.IntegerNumber ||
      this.contentType === ContentType.DecimalNumber
    ) {
      return;
    }

    // 输入英文
    if (this.currentMode === 2) {
      this.currentResult += " ";
      //  更新结果区
      this._updateResultShower();
    }
    // 输入中文
    else {
      // 有待选拼音时按空格键相当于选择第一个汉字
      if (this.currentPinyin.length > 0) {
        if (this.currentShowHanziArray.length > 0)
          this._onClickHanzi(this.currentShowHanziArray[0]);
      } else {
        this.currentResult += " ";
        //  更新结果区
        this._updateResultShower();
      }
    }
  }

  update(dt) {
    if (this.cursor) {
      if (this.cursor.material.visible && this.visible) {
        this.cursorTime += dt;
        if (this.cursorTime > this.cursorUpdateTime) {
          this.cursorTime = 0;

          this.cursor.visible = !this.cursor.visible;
        }
      }
    }
  }

  getClickable() {
    if (!this.visible) return [];
    let res = [];
    if (this.currentShowHanziArray) {
      res = res.concat(this.currentShowHanziArray);
    }
    if (this.latterArea.visible) res = res.concat(this.btns1);
    else if (this.symbolArea.visible) {
      for (var i = 0; i < this.btns2.length; i++) {
        if (this.btns2[i].visible) res.push(this.btns2[i]);
      }
    }
    if (this.commonArea.visible) res = res.concat(this.btns3);
    if (this.nextPageBtn && this.nextPageBtn.visible)
      res.push(this.nextPageBtn);
    if (this.prePageBtn && this.prePageBtn.visible) res.push(this.prePageBtn);
    return res;
  }

  /**
   * 单个拼音转单个汉字，例如输入 "a" 返回 "阿啊呵腌嗄吖锕"
   */
  _getSingleHanzi(pinyin) {
    return (
      pinyinUtil.dict.py2hz2[pinyin] || pinyinUtil.dict.py2hz[pinyin] || ""
    );
  }

  /**
   * 拼音转汉字
   * @param pinyin 需要转换的拼音，如 zhongguo
   * @return 返回一个数组，格式类似：[["中","重","种","众","终","钟","忠"], "zhong'guo"]
   */
  _getHanzi(pinyin) {
    var result = this._getSingleHanzi(pinyin);
    if (result) return [result.split(""), pinyin];
    var temp = "";
    for (var i = 0, len = pinyin.length; i < len; i++) {
      temp += pinyin[i];
      result = this._getSingleHanzi(temp);
      if (!result) continue;
      // flag表示如果当前能匹配到结果、并且往后5个字母不能匹配结果，因为最长可能是5个字母，如 zhuang
      var flag = false;
      if (i + 1 < pinyin.length) {
        for (var j = 1, len = pinyin.length; j <= 5 && i + j < len; j++) {
          if (this._getSingleHanzi(pinyin.substr(0, i + j + 1))) {
            flag = true;
            break;
          }
        }
      }
      if (!flag)
        return [
          result.split(""),
          pinyin.substr(0, i + 1) + "'" + pinyin.substr(i + 1),
        ];
    }
    return [[], ""]; // 理论上一般不会出现这种情况
  }

  /**
   * 拼音输入增加一个字符
   * @param {*} char
   */
  _addChar(char) {
    this.currentPinyin += char;

    this._refresh();
  }

  /**
   * 拼音输入去掉最后一个字符
   */
  _deleteChar() {
    this.currentPinyin = this.currentPinyin.slice(0, -1);

    this._refresh();
  }

  /**
   * 生成拼音对象
   */
  _createPinyinObj() {
    if (this.currentPinyinObj) {
      this._traverseDispose(this.currentPinyinObj);
      this.currentPinyinObj.removeFromParent();
      this.currentPinyinObj = null;
    }
    if (this.currentPinyin.length > 0) {
      this.currentPinyinObj = VUtil.createText(
        this.tempSelectHanzi + this.currentPinyin,
        this._textProps.textSize,
        0xffffff
      );

      this.pinyinShower.add(this.currentPinyinObj);
    }
  }

  /**
   * 刷新拼音显示
   */
  _refresh() {
    if (this.hanziPageMap) {
      const values = this.hanziPageMap.values();
      for (var value of values) {
        this._traverseDispose(value);
      }
      this.hanziPageMap.clear();
    }

    if (this.currentPinyin.length === 0) {
      if (this.tempSelectHanzi.length > 0) {
        this.currentResult += this.tempSelectHanzi;
        this._updateResultShower();
      }

      this._resetPinyin();
      return;
    } else if (this.currentPinyin.length === 1) {
      this.pinyinShower.visible = true;
    }

    var temp = this._getHanzi(this.currentPinyin.replace(/'/g, ""));
    this.hanziResult = temp[0];
    if (this.currentPinyin !== temp[1] && temp[1].length > 0) {
      this.currentPinyin = temp[1];
    }
    this._createPinyinObj();

    var count = this.hanziResult.length;
    this.pageCurrent = 1;
    this.pageCount = Math.ceil(count / this.pageSize);
    if (this.pageCount > 1) this.nextPageBtn.visible = true;
    this._refreshPage();
  }

  /**
   * 刷新汉字备选区
   * @returns
   */
  _refreshPage() {
    if (this.currentShowHanziArray.length > 0) {
      for (var i = 0; i < this.currentShowHanziArray.length; i++) {
        this.currentShowHanziArray[i].visible = false;
        this.currentShowHanziArray[i].removeFromParent();
      }
      this.currentShowHanziArray = [];
    }

    if (!this.hanziResult || this.hanziResult.length <= 0) {
      this.hanziShower.visible = false;
      this.nextPageBtn.visible = false;
      this.prePageBtn.visible = false;
      return;
    }
    this.hanziShower.visible = true;

    if (this.hanziPageMap.has(this.pageCurrent)) {
      const array = this.hanziPageMap.get(this.pageCurrent);
      for (var i = 0; i < array.length; i++) {
        this.hanziShower.add(array[i]);
        array[i].visible = true;
        this.currentShowHanziArray.push(array[i]);
      }
      return;
    }

    var temp = this.hanziResult.slice(
      (this.pageCurrent - 1) * this.pageSize,
      this.pageCurrent * this.pageSize
    );

    const tempArray = [];
    this.hanziPageMap.set(this.pageCurrent, tempArray);

    const showerWidth = this._baseProps.width / 2;
    const cellSize = showerWidth / this.pageSize;

    for (var i = 0; i < temp.length; i++) {
      // 背景
      const hanziItem = new Mesh(this.hanziBgGeo, this.hanziMat);
      hanziItem.renderOrder = this._baseProps.renderOrder;
      hanziItem.position.z = 0.001;
      hanziItem.position.x = -showerWidth / 2 + i * cellSize + 0.1;
      hanziItem._text = temp[i];
      this.hanziShower.add(hanziItem);
      this.currentShowHanziArray.push(hanziItem);
      tempArray.push(hanziItem);
      hanziItem.onEcsyHoverEnter = (target) => {
        this._onElementHoverEnter(target);
      };
      hanziItem.onEcsyHoverExit = (target) => {
        this._onElementHoverExit(target);
      };
      hanziItem.onVRClick = (target) => {
        this._onClickHanzi(target);
      };
      if (this.vuilib) this.vuilib.addComponents(hanziItem, this.btnPriority);

      // text
      const textObj = VUtil.createText(
        temp[i],
        this._textProps.textSize,
        0xffffff,
        "normal"
      );
      textObj.renderOrder = this._baseProps.renderOrder;
      hanziItem.add(textObj);
    }
  }

  /**
   * 点击汉字备选区的汉字
   */
  _onClickHanzi(obj) {
    this.tempSelectHanzi += obj._text;

    var idx = this.currentPinyin.indexOf("'");
    if (idx > 0) {
      this.currentPinyin = this.currentPinyin.slice(idx + 1);
      this._refresh();
    } // 如果没有单引号，表示已经没有候选词了
    else {
      this.currentResult += this.tempSelectHanzi;
      this._updateResultShower();

      this._resetPinyin();
    }
  }

  /**
   * 重置拼音显示
   */
  _resetPinyin() {
    this.pinyinShower.visible = false;
    this.hanziResult = [];
    this.currentPinyin = "";
    this.hanziShower.visible = false;
    this.nextPageBtn.visible = false;
    this.prePageBtn.visible = false;
    this.tempSelectHanzi = "";

    if (this.currentShowHanziArray.length > 0) {
      for (var i = 0; i < this.currentShowHanziArray.length; i++) {
        this.currentShowHanziArray[i].visible = false;
        this.currentShowHanziArray[i].removeFromParent();
      }
      this.currentShowHanziArray = [];
    }

    if (this.hanziPageMap) {
      const values = this.hanziPageMap.values();
      for (var value of values) {
        this._traverseDispose(value);
      }
      this.hanziPageMap.clear();
    }
  }
}

export { ContentType, VSoftKeyboard };
